import React from 'react';
import ConfigurationOption from 'components/triggers/ConfigurationOption';
import CommentIcon from 'components/pure/utils/icon/CommentIcon';
import CloseIcon from '@material-ui/icons/Close';
import PersonIcon from 'components/pure/utils/icon/PersonIcon';
import CheckIcon from '@material-ui/icons/Check';
import BothIcon from '@material-ui/icons/Group';
import InternalIcon from '@material-ui/icons/GroupAdd';
import CallerIcon from '@material-ui/icons/RecordVoiceOver';
import {CallConditionType, KeywordTopicPhrase, SpeakerCondition} from 'records/CallTrigger';
import SelectField from 'components/pure/utils/form/SelectField';
import {RadioButton} from 'material-ui/RadioButton';
import RangeField from 'components/pure/utils/form/RangeField';
import TextAreaField from 'components/pure/utils/form/TextAreaField';
import {SearchIcon} from '@components/icon/SearchIcon';
import GenericLink from 'components/GenericLink';

export default class KeywordTopicSelection extends React.Component {
  constructor(props) {
    super(props);

    this.locationTypeChange = this.locationTypeChange.bind(this);

    this.state = {
      showLocationSelection: false,
    };
  }

  locationTypeChange(locationType) {
    const updatedCondition = this.props.condition.set('type', locationType);
    // if the location type changes, we need to change the Class type, not just the property type.
    // need to convert it to a JS object so the getCallCondition doesn't just return the given Immutable.Iterable
    const newCondition = CallConditionType.getCallCondition(updatedCondition.toJS());
    this.props.conditionChange(newCondition);
  }

  render() {
    const {topicKeywords, condition, filterWords} = this.props;
    const range =
      condition.type === CallConditionType.MENTIONED_AT ? condition.range : {start: 0, end: 20};

    const topics = topicKeywords
      ? filterWords // all topics.
          .map(w => ({label: w.word, value: w.id}))
          .toArray()
          .sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1))
      : [];

    const showLocationSelection =
      this.state.showLocationSelection ||
      condition.type !== CallConditionType.MENTIONED_AT_LEAST_ONCE;

    return (
      <div className="keyword-topic-select">
        <div className="configuration-group conversation-type">
          <ConfigurationOption
            label="Keyword"
            icon={<SearchIcon width={24} height={24} />}
            active={!topicKeywords}
            onClick={() => this.props.topicKeywordChange(false)}
          />
          <ConfigurationOption
            label="Topic"
            icon={<CommentIcon />}
            active={topicKeywords}
            onClick={() => this.props.topicKeywordChange(true)}
          />
        </div>
        <div className="configuration-group discussion-type">
          <ConfigurationOption
            label={topicKeywords ? 'Discussed' : 'Said'}
            icon={<CheckIcon />}
            active={!condition.negated}
            onClick={() => this.props.conditionChange(condition.set('negated', false))}
          />
          <ConfigurationOption
            label={topicKeywords ? 'Not Discussed' : 'Not Said'}
            icon={<CloseIcon />}
            active={condition.negated}
            onClick={() => this.props.conditionChange(condition.set('negated', true))}
          />
        </div>
        <div className="configuration-group user-type">
          <ConfigurationOption
            label="Caller"
            icon={<CallerIcon />}
            active={condition.speakerType === SpeakerCondition.CALLER}
            onClick={() =>
              this.props.conditionChange(condition.set('speakerType', SpeakerCondition.CALLER))
            }
          />
          <ConfigurationOption
            label="Internal"
            icon={<InternalIcon />}
            active={condition.speakerType === SpeakerCondition.INTERNAL}
            onClick={() =>
              this.props.conditionChange(condition.set('speakerType', SpeakerCondition.INTERNAL))
            }
          />
          <ConfigurationOption
            label="External"
            icon={<PersonIcon />}
            active={condition.speakerType === SpeakerCondition.EXTERNAL}
            onClick={() =>
              this.props.conditionChange(condition.set('speakerType', SpeakerCondition.EXTERNAL))
            }
          />
          <ConfigurationOption
            label="Any"
            icon={<BothIcon />}
            active={condition.speakerType === null}
            onClick={() => this.props.conditionChange(condition.set('speakerType', null))}
          />
        </div>
        {topicKeywords ? (
          <div className="topic-selection">
            <SelectField
              value={condition.keywordTopicPhrase.filterWordId}
              options={topics}
              tether={true}
              hideResetButton={true}
              placeholder={'Select topic'}
              renderOption={item => (
                <div className="simple-option">
                  <strong>{item.label}</strong>
                </div>
              )}
              onChange={value =>
                this.props.conditionChange(
                  condition.set('keywordTopicPhrase', new KeywordTopicPhrase({filterWordId: value}))
                )
              }
            />
          </div>
        ) : (
          <div className="keyword-text-area-field">
            <TextAreaField
              placeholder="Enter Keyword or Phrase"
              value={condition.keywordTopicPhrase.keyword}
              onChange={value =>
                this.props.conditionChange(
                  condition.set('keywordTopicPhrase', new KeywordTopicPhrase({keyword: value}))
                )
              }
              rows={2}
            />
          </div>
        )}
        {!showLocationSelection && (
          <div className="specify-location">
            <GenericLink onClick={() => this.setState({showLocationSelection: true})}>
              Specify location in call for {topicKeywords ? 'topic' : 'keyword'}
            </GenericLink>
          </div>
        )}
        {showLocationSelection && (
          <div className="location-selection">
            <span>Specify location in call</span>
            <div className="radio-select-field">
              <div className="radio-wrapper">
                <RadioButton
                  label="Anywhere within call"
                  checked={condition.type === CallConditionType.MENTIONED_AT_LEAST_ONCE}
                  onClick={() => this.locationTypeChange(CallConditionType.MENTIONED_AT_LEAST_ONCE)}
                />
              </div>
            </div>
            <div className="radio-select-field">
              <div className="radio-wrapper">
                <RadioButton
                  label="Specify range within call"
                  checked={condition.type === CallConditionType.MENTIONED_AT}
                  onClick={() => this.locationTypeChange(CallConditionType.MENTIONED_AT)}
                />
              </div>
              {condition.type === CallConditionType.MENTIONED_AT && (
                <div className="range-inputs">
                  <RangeField
                    start={range.start}
                    end={range.end}
                    onValueChange={range =>
                      this.props.conditionChange(condition.set('range', range))
                    }
                    formatLabel={_ => ''}
                    showInputRange={true}
                    suffix="%"
                    disabled={condition.type !== CallConditionType.MENTIONED_AT}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}
